import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  itemName: string
  isInCart: boolean
}

function ConfirmDeleteCartItemModal({ itemName, isInCart }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<boolean>()

  const handleClose = useCallback(() => {
    modalElement.resolve(false)
  }, [modalElement])

  const handleConfirm = useCallback(() => {
    modalElement.resolve(true)
  }, [modalElement])

  const title = intl.formatMessage({
    id: 'c-cQu0WGd0NlkkwXH5_K',
    defaultMessage: 'Delete item?',
    description: 'Title for the confirm delete cart item modal in trip planner cart panel',
  })

  const deleteLabel = intl.formatMessage({
    id: '-J5c9H1EMX25FW-Oz8Qa',
    defaultMessage: 'Delete',
    description: 'Primary action button label to confirm deleting a cart item',
  })

  const cancelLabel = intl.formatMessage({
    id: 'VMHTx1BOVANDzNC8g27W',
    defaultMessage: 'Cancel',
    description: 'Secondary action button label to cancel deleting a cart item',
  })

  const message = isInCart
    ? intl.formatMessage(
        {
          id: '-xUmpyepU6eebTuaFIag',
          defaultMessage: 'This will remove <bold>{itemName}</bold> from both your cart and itinerary.',
          description: 'Confirmation message when deleting an active cart item from the trip planner cart panel',
        },
        {
          itemName,
          bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
        },
      )
    : intl.formatMessage(
        {
          id: '50Sa-g9Vdth1YDXDzcB4',
          defaultMessage: 'This will remove <bold>{itemName}</bold> from your itinerary.',
          description: 'Confirmation message when deleting a non-cart item from the trip planner cart panel',
        },
        {
          itemName,
          bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
        },
      )

  return (
    <Modal
      title={title}
      primaryActionText={deleteLabel}
      secondaryActionText={cancelLabel}
      onPrimaryActionClick={handleConfirm}
      onSecondaryActionClick={handleClose}
      onClose={handleClose}
      tabletHeight="auto"
    >
      <BodyText variant="medium">
        {message}
      </BodyText>
    </Modal>
  )
}

export default ConfirmDeleteCartItemModal
